import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShellHeaderExtraProvider } from '../context/ShellHeaderExtraContext';
import logo from '../assets/rush-republic-logo-dashboard.jpg';
import './AppShell.css';

/**
 * Minimal top bar shared by the Dashboard and its placeholder tiles (Lead
 * Quality Score, Instagram Scheduler) -- RUSH REPUBLIC, Log out, and
 * (Dashboard only) Create account for Admin. Deliberately separate from
 * AppShell.js (used by Shoot Plans, Brands, Team, Freelancers, Models) so
 * trimming this bar down can never affect those pages. Reuses AppShell.css's
 * existing classes (same bar/brand/nav-item/logout styling) rather than new
 * CSS. `showCreateAccount` defaults true for the Dashboard itself; the
 * placeholder pages pass false since Create account belongs on the
 * Dashboard only, never duplicated elsewhere.
 */
export default function DashboardShell({ children, showCreateAccount = true }) {
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const headerExtraRef = useRef(null);
  // A ref alone doesn't trigger a re-render once attached, so children
  // relying on the context value (to portal into it) would see null forever
  // on first render -- this state mirrors the ref once it's actually mounted.
  const [headerExtraEl, setHeaderExtraEl] = useState(null);
  useEffect(() => setHeaderExtraEl(headerExtraRef.current), []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="rr-shell">
      <div className="rr-shell__bar">
        <button className="rr-shell__brand" onClick={() => navigate('/dashboard')}>
          <img src={logo} alt="" className="rr-shell__brand-logo" />
          Rush Republic
        </button>

        <div className="rr-shell__spacer" />

        {/* Portal target for route-aware header content a page needs to show
            in this same bar (e.g. Lead Quality Score's breadcrumb) -- see
            ShellHeaderExtraContext. Empty and invisible when nothing portals
            into it. */}
        <div ref={headerExtraRef} className="rr-shell__header-extra" />

        {showCreateAccount && isAdmin && (
          <Link to="/create-account" className="rr-shell__nav-item rr-shell__nav-link">
            Create account
          </Link>
        )}

        <div className="rr-shell__user">
          <button className="rr-shell__logout" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      <ShellHeaderExtraProvider value={headerExtraEl}>
        <div className="rr-shell__body">{children}</div>
      </ShellHeaderExtraProvider>
    </div>
  );
}
